import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineDocument,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService, machineService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const MeterReadingsPage = () => {
  const [readings, setReadings] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [formData, setFormData] = useState({
    machineId: '',
    meterType: '',
    readingValue: '',
    readingAt: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view meter readings');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [readingsData, machinesData] = await Promise.all([
        masterDataService.getMeterReadings(tenantId),
        machineService.getMachines(tenantId)
      ]);
      setReadings(readingsData || []);
      setMachines(machinesData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredReadings = readings.filter((reading) => {
    const machine = machines.find(m => m.machineId === reading.machineId);
    return machine?.machineName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           reading.meterType?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleAdd = () => {
    setEditingReading(null);
    const now = new Date();
    const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setFormData({
      machineId: '',
      meterType: '',
      readingValue: '',
      readingAt: localDateTime,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (reading) => {
    // Note: updateFn is null in config, so edit is not supported
    setError('Edit functionality is not available for meter readings.');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.machineId) {
      errors.machineId = 'Machine is required';
    }
    if (!formData.meterType.trim()) {
      errors.meterType = 'Meter type is required';
    }
    if (!formData.readingValue || isNaN(parseFloat(formData.readingValue))) {
      errors.readingValue = 'Reading value is required and must be a number';
    }
    if (!formData.readingAt) {
      errors.readingAt = 'Reading date/time is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const readingData = {
        tenantId: tenantId,
        machineId: parseInt(formData.machineId),
        meterType: formData.meterType.trim(),
        readingValue: parseFloat(formData.readingValue),
        readingAt: new Date(formData.readingAt),
      };

      await masterDataService.createMeterReading(readingData);

      await loadData();
      setShowModal(false);
      setFormData({ machineId: '', meterType: '', readingValue: '', readingAt: '' });
      setEditingReading(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save meter reading. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingReading(null);
    setFormData({ machineId: '', meterType: '', readingValue: '', readingAt: '' });
    setFormErrors({});
  };

  const getMachineName = (machineId) => {
    if (!machineId) return '-';
    const machine = machines.find(m => m.machineId === machineId);
    return machine ? `${machine.machineCode} - ${machine.machineName}` : '-';
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading meter readings..." />
      </div>
    );
  }

  return (
    <div className="pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Meter Readings</h2>
          <p>Manage meter readings for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Reading
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search readings..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredReadings.length} {filteredReadings.length === 1 ? 'reading' : 'readings'}
          </div>
        </div>

        {filteredReadings.length === 0 ? (
          <div className="empty-state">
            <HiOutlineDocument className="empty-icon" />
            <h3>No readings found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first meter reading'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Reading
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Machine</th>
                <th>Meter Type</th>
                <th>Reading Value</th>
                <th>Reading At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredReadings.map((reading) => (
                <tr key={reading.meterId}>
                  <td>{reading.meterId}</td>
                  <td>{getMachineName(reading.machineId)}</td>
                  <td>{reading.meterType}</td>
                  <td>{reading.readingValue}</td>
                  <td>{reading.readingAt ? new Date(reading.readingAt).toLocaleString() : '-'}</td>
                  <td>
                    <div className="action-buttons">
                      {/* Note: Edit and Delete are not available for meter readings */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Add New Meter Reading"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="meter-reading-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="meter-reading-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="machineId">
                Machine <span className="required">*</span>
              </label>
              <select
                id="machineId"
                value={formData.machineId}
                onChange={(e) =>
                  setFormData({ ...formData, machineId: e.target.value })
                }
                className={formErrors.machineId ? 'error' : ''}
              >
                <option value="">Select Machine</option>
                {machines.map((machine) => (
                  <option key={machine.machineId} value={machine.machineId.toString()}>
                    {machine.machineCode} - {machine.machineName}
                  </option>
                ))}
              </select>
              {formErrors.machineId && (
                <span className="field-error">{formErrors.machineId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="meterType">
                Meter Type <span className="required">*</span>
              </label>
              <input
                type="text"
                id="meterType"
                value={formData.meterType}
                onChange={(e) =>
                  setFormData({ ...formData, meterType: e.target.value })
                }
                className={formErrors.meterType ? 'error' : ''}
                placeholder="Enter meter type"
              />
              {formErrors.meterType && (
                <span className="field-error">{formErrors.meterType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="readingValue">
                Reading Value <span className="required">*</span>
              </label>
              <input
                type="number"
                id="readingValue"
                value={formData.readingValue}
                onChange={(e) =>
                  setFormData({ ...formData, readingValue: e.target.value })
                }
                className={formErrors.readingValue ? 'error' : ''}
                placeholder="Enter reading value"
                step="0.01"
              />
              {formErrors.readingValue && (
                <span className="field-error">{formErrors.readingValue}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="readingAt">
                Reading At <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                id="readingAt"
                value={formData.readingAt}
                onChange={(e) =>
                  setFormData({ ...formData, readingAt: e.target.value })
                }
                className={formErrors.readingAt ? 'error' : ''}
              />
              {formErrors.readingAt && (
                <span className="field-error">{formErrors.readingAt}</span>
              )}
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default MeterReadingsPage;
